import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useVehicles } from '@/hooks/useVehicles'
import { lumi } from '@/lib/lumi'
import {ChevronLeft, Upload, Save, Car, Zap, X, Image as ImageIcon} from 'lucide-react'

export default function VehicleForm() {
  const { id } = useParams()
  const { isAdmin } = useAuth()
  const navigate = useNavigate()
  const { vehicles, fetchVehicles, createVehicle, updateVehicle } = useVehicles()
  const [loading, setLoading] = useState(false)
  const [uploadingMain, setUploadingMain] = useState(false)
  const [uploadingGallery, setUploadingGallery] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    model: '',
    category: 'Citadine',
    registration: '',
    seats: 5,
    transmission: 'Manuelle',
    fuel: 'Essence',
    mileage: 0,
    pricePerDay: 0,
    pricePerHalfDay: 0,
    pricePerWeek: 0,
    pricePerKm: 0,
    includedKm: 200,
    extraKmPrice: 0.50,
    deposit: 500,
    imageUrl: '',
    images: [] as string[],
    features: [] as string[],
    status: 'disponible',
  })

  useEffect(() => {
    if (!isAdmin) {
      navigate('/')
      return
    }

    if (id) {
      fetchVehicles()
    }
  }, [isAdmin, id])

  useEffect(() => {
    if (id && vehicles.length > 0) {
      const vehicle = vehicles.find((v) => v._id === id)
      if (vehicle) {
        setFormData({
          name: vehicle.name || '',
          brand: vehicle.brand || '',
          model: vehicle.model || '',
          category: vehicle.category || 'Citadine',
          registration: vehicle.registration || '',
          seats: vehicle.seats || 5,
          transmission: vehicle.transmission || 'Manuelle',
          fuel: vehicle.fuel || 'Essence',
          mileage: vehicle.mileage || 0,
          pricePerDay: vehicle.pricePerDay || 0,
          pricePerHalfDay: vehicle.pricePerHalfDay || 0,
          pricePerWeek: vehicle.pricePerWeek || 0,
          pricePerKm: vehicle.pricePerKm || 0,
          includedKm: vehicle.includedKm || 200,
          extraKmPrice: vehicle.extraKmPrice || 0.50,
          deposit: vehicle.deposit || 500,
          imageUrl: vehicle.imageUrl || '',
          images: vehicle.images || [],
          features: vehicle.features || [],
          status: vehicle.status || 'disponible',
        })
      }
    }
  }, [id, vehicles])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const vehicleData = {
        ...formData,
        seats: Number(formData.seats),
        mileage: Number(formData.mileage),
        pricePerDay: Number(formData.pricePerDay),
        pricePerHalfDay: Number(formData.pricePerHalfDay),
        pricePerWeek: Number(formData.pricePerWeek),
        pricePerKm: Number(formData.pricePerKm),
        includedKm: Number(formData.includedKm),
        extraKmPrice: Number(formData.extraKmPrice),
        deposit: Number(formData.deposit),
      }

      if (id) {
        await updateVehicle(id, vehicleData)
        alert('Véhicule modifié avec succès')
      } else {
        await createVehicle(vehicleData)
        alert('Véhicule ajouté avec succès')
      }
      navigate('/admin')
    } catch (error: any) {
      console.error('Error saving vehicle:', error)
      alert('Erreur: ' + (error.message || 'Une erreur est survenue'))
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleArrayChange = (field: 'features' | 'images', value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value.split(',').map((item) => item.trim()).filter(Boolean),
    }))
  }

  const handleMainImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    setUploadingMain(true)
    try {
      const results = await lumi.tools.file.upload(Array.from(files))
      const uploaded = results.find(r => r.fileUrl)
      if (uploaded?.fileUrl) {
        setFormData(prev => ({ ...prev, imageUrl: uploaded.fileUrl! }))
      } else {
        alert('Erreur lors de l\'upload: ' + (results[0]?.uploadError || 'Erreur inconnue'))
      }
    } catch (error: any) {
      alert('Erreur lors de l\'upload: ' + error.message)
    } finally {
      setUploadingMain(false)
    }
  }

  const handleGalleryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    setUploadingGallery(true)
    try {
      const results = await lumi.tools.file.upload(Array.from(files))
      const uploadedUrls = results.filter(r => r.fileUrl).map(r => r.fileUrl!)
      if (uploadedUrls.length > 0) {
        setFormData(prev => ({ ...prev, images: [...prev.images, ...uploadedUrls] }))
      }
      const errors = results.filter(r => r.uploadError)
      if (errors.length > 0) {
        alert('Certains fichiers n\'ont pas pu être téléchargés: ' + errors.map(e => e.uploadError).join(', '))
      }
    } catch (error: any) {
      alert('Erreur lors de l\'upload: ' + error.message)
    } finally {
      setUploadingGallery(false)
    }
  }

  const removeGalleryImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }))
  }

  if (!isAdmin) {
    return null
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 text-yellow-400 hover:text-yellow-300 mb-4 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            Retour au tableau de bord
          </button>
          <div className="flex items-center gap-4">
            <div className="bg-gradient-to-br from-yellow-400 to-yellow-500 p-4 rounded-2xl shadow-lg">
              <Car className="w-10 h-10 text-black" />
            </div>
            <div>
              <h1 className="text-4xl font-black bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-400 bg-clip-text text-transparent">
                {id ? 'Modifier le véhicule' : 'Ajouter un véhicule'}
              </h1>
              <p className="text-gray-400 mt-1">Remplissez tous les champs requis</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl border-2 border-yellow-400/30 p-8">
          {/* Informations de base */}
          <div className="mb-8">
            <h2 className="text-2xl font-black text-white mb-6 flex items-center gap-2">
              <Zap className="w-6 h-6 text-yellow-400" />
              Informations de base
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Nom du véhicule *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 focus:border-yellow-400 rounded-lg text-white transition-all outline-none"
                  placeholder="Ex: Citadine Économique"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Marque *</label>
                <input
                  type="text"
                  name="brand"
                  value={formData.brand}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 focus:border-yellow-400 rounded-lg text-white transition-all outline-none"
                  placeholder="Ex: Renault"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Modèle *</label>
                <input
                  type="text"
                  name="model"
                  value={formData.model}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 focus:border-yellow-400 rounded-lg text-white transition-all outline-none"
                  placeholder="Ex: Clio"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Catégorie *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 focus:border-yellow-400 rounded-lg text-white transition-all outline-none"
                >
                  <option value="Citadine">Citadine</option>
                  <option value="Berline">Berline</option>
                  <option value="SUV">SUV</option>

                  <option value="Premium">Premium</option>
                  <option value="Monospace">Monospace</option>
                  <option value="Fourgon">Fourgon</option>
                  <option value="Plateau">Plateau</option>

                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Immatriculation</label>
                <input
                  type="text"
                  name="registration"
                  value={formData.registration}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 focus:border-yellow-400 rounded-lg text-white transition-all outline-none"
                  placeholder="Ex: VD-001"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Nombre de places *</label>
                <input
                  type="number"
                  name="seats"
                  value={formData.seats}
                  onChange={handleChange}
                  required
                  min="2"
                  max="9"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 focus:border-yellow-400 rounded-lg text-white transition-all outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Transmission *</label>
                <select
                  name="transmission"
                  value={formData.transmission}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 focus:border-yellow-400 rounded-lg text-white transition-all outline-none"
                >
                  <option value="Manuelle">Manuelle</option>
                  <option value="Automatique">Automatique</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Carburant *</label>
                <select
                  name="fuel"
                  value={formData.fuel}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 focus:border-yellow-400 rounded-lg text-white transition-all outline-none"
                >
                  <option value="Essence">Essence</option>
                  <option value="Diesel">Diesel</option>
                  <option value="Hybride">Hybride</option>
                  <option value="Électrique">Électrique</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Kilométrage</label>
                <input
                  type="number"
                  name="mileage"
                  value={formData.mileage}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 focus:border-yellow-400 rounded-lg text-white transition-all outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Statut</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 focus:border-yellow-400 rounded-lg text-white transition-all outline-none"
                >
                  <option value="disponible">Disponible (visible + réservable)</option>
                  <option value="indisponible">Indisponible (visible mais non réservable)</option>
                  <option value="cache">Caché (invisible sur le site)</option>
                  <option value="rented">Loué</option>
                  <option value="maintenance">Maintenance</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  <span className="text-emerald-400">Disponible</span> = visible et réservable • 
                  <span className="text-orange-400"> Indisponible</span> = visible mais pas réservable • 
                  <span className="text-red-400"> Caché</span> = invisible sur le site
                </p>
              </div>
            </div>
          </div>

          {/* Tarification */}
          <div className="mb-8">
            <h2 className="text-2xl font-black text-white mb-6 flex items-center gap-2">
              <Zap className="w-6 h-6 text-yellow-400" />
              Tarification
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Prix par demi-journée (CHF) *</label>
                <input
                  type="number"
                  name="pricePerHalfDay"
                  value={formData.pricePerHalfDay}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 focus:border-yellow-400 rounded-lg text-white transition-all outline-none"
                  placeholder="Ex: 45"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Prix par jour (CHF) *</label>
                <input
                  type="number"
                  name="pricePerDay"
                  value={formData.pricePerDay}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 focus:border-yellow-400 rounded-lg text-white transition-all outline-none"
                  placeholder="Ex: 80"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Prix par semaine / 7 jours (CHF) *</label>
                <input
                  type="number"
                  name="pricePerWeek"
                  value={formData.pricePerWeek}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 focus:border-yellow-400 rounded-lg text-white transition-all outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Km inclus par jour *</label>
                <input
                  type="number"
                  name="includedKm"
                  value={formData.includedKm}
                  onChange={handleChange}
                  required
                  min="0"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 focus:border-yellow-400 rounded-lg text-white transition-all outline-none"
                  placeholder="Ex: 200"
                />
                <p className="text-xs text-gray-500 mt-1">0 = kilométrage illimité</p>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Prix par km supplémentaire (CHF) *</label>
                <input
                  type="number"
                  name="extraKmPrice"
                  value={formData.extraKmPrice}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 focus:border-yellow-400 rounded-lg text-white transition-all outline-none"
                  placeholder="Ex: 0.50"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Caution (CHF) *</label>
                <input
                  type="number"
                  name="deposit"
                  value={formData.deposit}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 focus:border-yellow-400 rounded-lg text-white transition-all outline-none"
                />
              </div>
            </div>
          </div>

          {/* Images et caractéristiques */}
          <div className="mb-8">
            <h2 className="text-2xl font-black text-white mb-6 flex items-center gap-2">
              <Upload className="w-6 h-6 text-yellow-400" />
              Images et caractéristiques
            </h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Image principale *</label>
                <div className="space-y-4">
                  {formData.imageUrl && (
                    <div className="relative w-full h-48 rounded-xl overflow-hidden border-2 border-yellow-400/30">
                      <img src={formData.imageUrl} alt="Aperçu" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <label className="block">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleMainImageUpload}
                      className="hidden"
                      disabled={uploadingMain}
                    />
                    <div className="flex items-center justify-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black px-6 py-3 rounded-xl hover:shadow-2xl hover:shadow-yellow-400/50 transition-all font-bold cursor-pointer hover:scale-105 disabled:opacity-50">
                      {uploadingMain ? (
                        <>
                          <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                          Upload en cours...
                        </>
                      ) : (
                        <>
                          <Upload className="w-5 h-5" />
                          {formData.imageUrl ? 'Changer l\'image principale' : 'Importer l\'image principale'}
                        </>
                      )}
                    </div>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Galerie d'images supplémentaires</label>
                <div className="space-y-4">
                  {formData.images.length > 0 && (
                    <div className="grid grid-cols-3 gap-4">
                      {formData.images.map((url, index) => (
                        <div key={index} className="relative group">
                          <img src={url} alt={`Image ${index + 1}`} className="w-full h-32 object-cover rounded-lg border-2 border-gray-700" />
                          <button
                            type="button"
                            onClick={() => removeGalleryImage(index)}
                            className="absolute top-2 right-2 bg-red-600 hover:bg-red-500 text-white p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all shadow-lg"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <label className="block">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleGalleryUpload}
                      className="hidden"
                      disabled={uploadingGallery}
                    />
                    <div className="flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-xl transition-all font-bold cursor-pointer hover:scale-105">
                      {uploadingGallery ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Upload en cours...
                        </>
                      ) : (
                        <>
                          <ImageIcon className="w-5 h-5" />
                          Ajouter des images à la galerie
                        </>
                      )}
                    </div>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Caractéristiques (séparées par des virgules)</label>
                <textarea
                  value={formData.features.join(', ')}
                  onChange={(e) => handleArrayChange('features', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 focus:border-yellow-400 rounded-lg text-white transition-all outline-none"
                  placeholder="Climatisation, GPS, Bluetooth"
                />
              </div>
            </div>
          </div>

          {/* Boutons */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black px-8 py-4 rounded-xl hover:shadow-2xl hover:shadow-yellow-400/50 transition-all font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {id ? 'Modifier' : 'Ajouter'} le véhicule
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => navigate('/admin')}
              className="px-8 py-4 bg-gray-700 hover:bg-gray-600 text-white rounded-xl transition-all font-bold"
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
